import {
    ArrowLeftOutlined,
    DownloadOutlined,
    EyeOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Spin, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exportDocument, getDocument } from '../api';
import DocOutline, { useDocOutline } from '../components/DocOutline';
import PreviewMarkdown from '../StepForm/PreviewStep/PreviewMarkdown';
import styles from './index.module.scss';

/** 只读预览：/order/preview?no=&doc=（正文 main 或附加服务 addon:xxx），左侧带正文目录 */
export default function OrderPreview() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const no = String(params.get('no') || '').trim();
    const doc = String(params.get('doc') || 'main').trim();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [tocOpen, setTocOpen] = useState(false);
    const bodyRef = useRef(null);

    const load = useCallback(async () => {
        if (!no) {
            setError('缺少订单号，请从订单页进入预览');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await getDocument(no, doc);
            setData(res || { title: '', markdown: '' });
        } catch (err) {
            setError(err?.response?.data?.msg || err?.message || '文档不存在或暂不可预览');
        } finally {
            setLoading(false);
        }
    }, [no, doc]);

    useEffect(() => {
        load();
    }, [load]);

    const title = data?.title || '文档预览';
    const markdown = data?.markdown || '';
    const ready = !(loading && !data) && !error;
    // 目录从渲染出来的正文标题收集，正文变了重新收集
    const outline = useDocOutline(bodyRef, [markdown, ready]);

    useEffect(() => {
        if (!data?.title) return undefined;
        const prev = document.title;
        document.title = `${data.title} - 预览`;
        return () => {
            document.title = prev;
        };
    }, [data?.title]);

    const handleDownload = async () => {
        if (downloading || !no) return;
        setDownloading(true);
        try {
            await exportDocument(`/ai/order/export?${new URLSearchParams({ no, doc }).toString()}`, title);
        } catch (err) {
            message.error(err?.message || '下载失败，请稍后重试');
        } finally {
            setDownloading(false);
        }
    };

    const backToOrder = () => navigate(no ? `/order/result?no=${encodeURIComponent(no)}` : '/order/result');

    const handleOutlineSelect = (id) => {
        setTocOpen(false);
        outline.scrollTo(id);
    };

    return (
        <div className={styles.page}>
            <header className={styles.bar}>
                <div className={styles.barLeft}>
                    <button type="button" className={styles.backBtn} onClick={backToOrder}>
                        <ArrowLeftOutlined />
                        <span className={styles.backText}>返回订单</span>
                    </button>
                    <span className={styles.tag}>
                        <EyeOutlined /> 只读预览
                    </span>
                    <h1 className={styles.barTitle} title={title}>
                        {title}
                    </h1>
                </div>
                <div className={styles.barRight}>
                    <Button
                        icon={<UnorderedListOutlined />}
                        className={styles.tocBtn}
                        onClick={() => setTocOpen(true)}
                        disabled={!ready}
                        aria-label="打开目录"
                    >
                        目录
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={load} disabled={loading} className={styles.refreshBtn}>
                        <span className={styles.btnText}>刷新</span>
                    </Button>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={downloading}
                        disabled={!no || !!error}
                        onClick={handleDownload}
                    >
                        下载 Word
                    </Button>
                </div>
            </header>

            <main className={styles.scroll}>
                {loading && !data ? (
                    <div className={styles.state}>
                        <Spin /> 正在加载文档…
                    </div>
                ) : error ? (
                    <div className={styles.state}>
                        <p className={styles.error}>{error}</p>
                        <div className={styles.stateActions}>
                            <Button onClick={load}>重试</Button>
                            <Button type="primary" onClick={backToOrder}>
                                返回订单
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className={styles.workspace}>
                        <aside className={styles.side}>
                            <DocOutline
                                items={outline.items}
                                activeId={outline.activeId}
                                onSelect={outline.scrollTo}
                                emptyText={markdown.trim() ? '正文里还没有章节标题' : '正文生成后这里显示目录'}
                            />
                        </aside>
                        <article className={styles.paper}>
                            <h1 className={styles.paperTitle}>{title}</h1>
                            <div ref={bodyRef}>
                                {markdown.trim() ? (
                                    <PreviewMarkdown content={markdown} rawHtml placeholderText="该图表尚未生成" />
                                ) : (
                                    <div className={styles.state}>
                                        正文还在生成中，请稍后刷新
                                        <Button onClick={load} icon={<ReloadOutlined />}>
                                            刷新
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <div className={styles.foot}>内容由 AI 生成，仅供参考；只读预览，如需修改请下载 Word</div>
                        </article>
                    </div>
                )}
            </main>

            <Drawer
                open={tocOpen}
                onClose={() => setTocOpen(false)}
                placement="left"
                width="min(86vw, 320px)"
                title="目录"
                rootClassName={styles.drawer}
            >
                <DocOutline items={outline.items} activeId={outline.activeId} onSelect={handleOutlineSelect} />
            </Drawer>
        </div>
    );
}
